import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { X, Loader2, CheckCircle2, Search, Printer, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate, fmtTimeRange, fmtDateTime } from "@/lib/format";

const METHODS = [
  ["cash", "Cash"], ["stripe", "Card (Stripe)"], ["gift_card", "Gift Card"], ["promotional", "Promotional"],
  ["complimentary", "Complimentary"], ["trade_barter", "Trade/Barter"], ["other", "Other"],
];

export const invalidatePayments = (qc) => {
  ["pos-u-outstanding", "pos-u-person", "pos-u-booking", "pos-u-search", "pos-appointments", "pos-balance-due", "pos-balances-today", "pos-balances-count", "pos-dashboard"]
    .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
};

const printReceipt = (b, line) => {
  const w = window.open("", "_blank", "width=420,height=560");
  if (!w) return;
  w.document.write(`<html><head><title>Receipt</title></head><body style="font-family:Arial;padding:24px;color:#1A242B">
    <h2 style="font-family:Georgia;color:#144F75;margin:0">Enhancing Your World</h2><p style="color:#8FA89B;margin:4px 0 16px">Payment receipt</p>
    <p><b>${b.title}</b><br/>${fmtDate(b.date)} ${fmtTimeRange(b.time)}</p><p>Client: ${b.customer_name || b.customer_email || ""}</p>
    <p>${line.method_label}: <b>${money(line.amount)}</b></p>${line.change ? `<p>Change given: ${money(line.change)}</p>` : ""}
    <p>Balance remaining: ${money(line.balance_after)}</p><p style="font-size:12px;color:#4A5D6B">${fmtDateTime(line.created_at)} · recorded by ${line.recorded_by}</p>
    <p style="font-size:12px">Thank you for showing up for your well-being.</p></body></html>`);
  w.document.close(); w.print();
};

function CardForm({ b, amount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data: it } = await api.post("/pos/u/payments/card/intent", { kind: b.kind, ref_id: b.id, amount: amount ? Number(amount) : null });
      if (it.status === "succeeded") return onPaid(it.payment);
      const r = await stripe.confirmCardPayment(it.client_secret, { payment_method: { card: elements.getElement(CardElement) } });
      if (r.error) throw new Error(r.error.message);
      const { data } = await api.post("/pos/u/payments/card/confirm", { kind: b.kind, ref_id: b.id, payment_intent_id: it.payment_intent_id });
      onPaid(data.payment);
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-[#E5E0D8] bg-white px-3 py-3" data-testid="pay-card-element"><CardElement options={{ hidePostalCode: true }} /></div>
      <button onClick={pay} disabled={busy || !stripe} data-testid="pay-card-submit" className="eyw-btn w-full disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />} Charge card</button>
    </div>
  );
}

function GiftCheck({ code, setCode, card, setCard }) {
  const check = async () => {
    try { const { data } = await api.get(`/pos/giftcards/${encodeURIComponent(code.trim())}`); setCard(data); }
    catch (e) { setCard({ error: formatApiError(e.response?.data?.detail) }); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCard(null); }} placeholder="EYW-XXXX-XXXX-XXXX" data-testid="pay-gift-code" className="eyw-input flex-1 font-mono uppercase" />
        <button type="button" onClick={check} disabled={!code.trim()} data-testid="pay-gift-check" className="eyw-btn-ghost text-sm"><Search size={14} /> Check</button>
      </div>
      {card?.error && <p className="text-xs text-red-600 mt-1" data-testid="pay-gift-error">{card.error}</p>}
      {card && !card.error && <p className="text-xs text-[#5C8A72] mt-1" data-testid="pay-gift-balance">Valid · {money(card.balance)} available</p>}
    </div>
  );
}

// One screen for every way a client can pay: cash, card, gift card, promo, comp, trade, other. Split payments = several lines.
export default function UnifiedPaymentModal({ kind, refId, onClose }) {
  const qc = useQueryClient();
  const { data: b, isLoading, refetch } = useQuery({ queryKey: ["pos-u-booking", kind, refId], queryFn: () => api.get(`/pos/u/booking/${kind}/${refId}`).then((r) => r.data) });
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [received, setReceived] = useState("");
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [card, setCard] = useState(null);
  const [settle, setSettle] = useState(false);
  const [emailReceipt, setEmailReceipt] = useState(true);
  const [useSaved, setUseSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const stripePromise = useMemo(() => (b?.publishable_key ? loadStripe(b.publishable_key) : null), [b?.publishable_key]);
  const due = b?.balance_due || 0;
  const cashAmt = Math.min(due, Number(amount || received || due));
  const change = method === "cash" && received ? Math.max(0, Number(received) - cashAmt) : 0;

  const done = (line) => {
    const left = line.balance_after;
    setLast({ ...line, change });
    toast.success(left > 0 ? `${money(line.amount)} recorded — ${money(left)} still due` : `${money(line.amount)} recorded — paid in full`);
    invalidatePayments(qc); refetch();
    setAmount(""); setReceived(""); setNote(""); setCode(""); setCard(null); setSettle(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      if (method === "stripe" && useSaved) {
        const { data } = await api.post("/pos/u/payments/card/intent", { kind, ref_id: refId, amount: amount ? Number(amount) : null, use_saved: true });
        if (data.status !== "succeeded") throw new Error("The saved card needs extra verification — use a new card instead.");
        return done(data.payment);
      }
      const amt = method === "cash" ? cashAmt : amount === "" ? null : Number(amount);
      const { data } = await api.post("/pos/u/payments", { kind, ref_id: refId, method, amount: amt, note, gift_card_code: method === "gift_card" ? code : "", settle_remaining: settle, receipt_email: emailReceipt });
      done(data.payment);
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/40 backdrop-blur-sm grid place-items-center px-4" data-testid="unified-payment-modal">
      <div className="w-full max-w-xl bg-[#F9F7F3] rounded-3xl shadow-2xl p-6 sm:p-7 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="eyw-label">Take a payment</p>
            <h3 className="font-serif text-xl text-[#1A242B]">{b?.title || "Booking"}</h3>
            {b && <p className="text-xs text-[#4A5D6B]">{b.customer_name || b.customer_email} · {fmtDate(b.date)} {fmtTimeRange(b.time)}{b.enhancer_name ? ` · ${b.enhancer_name}` : ""}</p>}
          </div>
          <button onClick={onClose} data-testid="pay-close"><X size={18} /></button>
        </div>
        {isLoading || !b ? <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div> : (
          <>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Total</p><p className="font-serif text-lg">{money(b.total_price)}</p></div>
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Paid</p><p className="font-serif text-lg text-[#5C8A72]" data-testid="pay-paid">{money(b.amount_paid)}</p></div>
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Due</p><p className="font-serif text-lg text-[#B5794E]" data-testid="pay-due">{money(due)}</p></div>
            </div>
            {b.lines.length > 0 && (
              <ul className="mt-3 text-xs text-[#4A5D6B] space-y-1" data-testid="pay-lines">
                {b.lines.map((l, i) => <li key={i} className="flex justify-between"><span>{l.method_label}{l.gift_card_code ? ` · ${l.gift_card_code}` : ""}{l.note ? ` — ${l.note}` : ""}</span><span>{money(l.amount)}</span></li>)}
              </ul>
            )}
            {last && (
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[#5C8A72]/10 px-3 py-2 text-sm text-[#3E6B54]" data-testid="pay-last">
                <span><CheckCircle2 size={14} className="inline" /> {last.method_label} {money(last.amount)}{last.change ? ` · change ${money(last.change)}` : ""}</span>
                <button onClick={() => printReceipt(b, last)} data-testid="pay-print-receipt" className="text-xs underline inline-flex items-center gap-1"><Printer size={12} /> Print receipt</button>
              </div>
            )}
            {due <= 0 ? (
              <p className="mt-5 text-sm text-[#5C8A72] flex items-center gap-2" data-testid="pay-paid-full"><CheckCircle2 size={16} /> Paid in full. Nothing left to collect.</p>
            ) : (
              <div className="mt-5 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {METHODS.map(([k, l]) => (
                    <button key={k} onClick={() => setMethod(k)} data-testid={`pay-method-${k}`} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${method === k ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#144F75]/25 text-[#144F75] hover:bg-[#144F75]/[0.05]"}`}>{l}</button>
                  ))}
                </div>
                {method === "cash" && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min="0" step="0.01" value={received} onChange={(e) => setReceived(e.target.value)} placeholder={`Cash received (${money(due)})`} data-testid="pay-cash-received" className="eyw-input" />
                    <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Apply (defaults to due)" data-testid="pay-amount" className="eyw-input" />
                    {change > 0 && <p className="col-span-2 text-sm text-[#144F75]" data-testid="pay-cash-change">Change to give: <b>{money(change)}</b></p>}
                  </div>
                )}
                {method === "gift_card" && <GiftCheck code={code} setCode={setCode} card={card} setCard={setCard} />}
                {method !== "cash" && (
                  <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" className="eyw-input w-full"
                    placeholder={method === "gift_card" ? "Amount to apply (blank = most available)" : method === "stripe" ? `Amount to charge (${money(due)})` : "Amount applied"} />
                )}
                {method === "stripe" ? (
                  !b.stripe_ready ? <p className="text-sm text-[#B5794E]" data-testid="pay-card-unavailable">Card payments aren't set up yet — ask an admin to connect Stripe.</p> : (
                    <>
                      {b.has_card_on_file && (
                        <label className="flex gap-2 text-xs text-[#4A5D6B]"><input type="checkbox" checked={useSaved} onChange={(e) => setUseSaved(e.target.checked)} data-testid="pay-use-saved" /> Charge the card on file</label>
                      )}
                      {useSaved ? (
                        <button onClick={submit} disabled={busy} data-testid="pay-saved-submit" className="eyw-btn w-full"><CreditCard size={16} /> Charge card on file</button>
                      ) : stripePromise && <Elements stripe={stripePromise}><CardForm b={b} amount={amount} onPaid={done} /></Elements>}
                    </>
                  )
                ) : (
                  <>
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — promo name, trade details…" data-testid="pay-note" className="eyw-input w-full" />
                    {method !== "cash" && (
                      <label className="flex gap-2 text-xs text-[#4A5D6B]"><input type="checkbox" checked={settle} onChange={(e) => setSettle(e.target.checked)} data-testid="pay-settle" /> Mark the balance settled even if this doesn't cover it all</label>
                    )}
                    <label className="flex gap-2 text-xs text-[#4A5D6B]"><input type="checkbox" checked={emailReceipt} onChange={(e) => setEmailReceipt(e.target.checked)} data-testid="pay-email-receipt" /> Email a receipt to the client</label>
                    <button onClick={submit} disabled={busy || (method === "gift_card" && !(card && !card.error)) || (!["cash", "gift_card"].includes(method) && !amount)} data-testid="pay-submit" className="eyw-btn w-full disabled:opacity-50">
                      {busy && <Loader2 size={16} className="animate-spin" />} Record {METHODS.find((m) => m[0] === method)[1].toLowerCase()} payment
                    </button>
                  </>
                )}
                <p className="text-[11px] text-[#8FA89B]">Each payment is saved as its own permanent line. Combine methods to split a balance.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
